import React, { useRef, useState } from 'react';
import { MdBackup, MdDownload, MdRestore, MdUpload, MdSchedule, MdDeleteForever } from 'react-icons/md';
import useToastWithSound from '../../hooks/useToastWithSound';
import api, { session } from '../../ui/api';
import useData from '../useData';
import PageShell, { Button } from '../../ui/PageShell';
import Card from '../../ui/Card';
import Table from '../../ui/Table';
import Modal, { ConfirmModal } from '../../ui/Modal';
import { Input } from '../../ui/Form';
import './backups.css';

/**
 * النسخ الاحتياطي — the database is copied to Cloudflare R2 every day at
 * 07:00 and 18:00 (api/src/lib/backup.js + cron). Here the manager sees
 * those copies, takes one now, downloads one to the device, sends a
 * saved file back, and restores.
 */

const fmt = (iso) => (iso
  ? new Date(iso).toLocaleString('ar-SY', { dateStyle: 'medium', timeStyle: 'short' })
  : '—');
const size = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const TONE = { auto: 'info', manual: 'success', pre_restore: 'warning', uploaded: 'neutral', pre_reset: 'danger' };
// Typed by the manager in the second confirmation, word for word.
const RESET_PHRASE = 'حذف كل البيانات';

export default function Backups() {
  const toast = useToastWithSound();
  const fileRef = useRef(null);
  const { data, loading, error, reload } = useData(() => api.backups(), []);
  const [busy, setBusy] = useState('');       // '' | 'create' | 'upload' | 'restore' | name
  const [restoreFor, setRestoreFor] = useState(null);
  // البدء من جديد: 0 closed, 1 first warning, 2 type the phrase
  const [resetStep, setResetStep] = useState(0);
  const [resetTyped, setResetTyped] = useState('');

  const list = data?.backups || [];
  const last = list[0];

  const create = async () => {
    setBusy('create');
    try {
      await api.createBackup();
      toast.success('تم حفظ نسخة احتياطية الآن');
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(''); }
  };

  const download = async (row) => {
    setBusy(row.name);
    try { await api.downloadBackup(row.name); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(''); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy('upload');
    try {
      await api.uploadBackup(file);
      toast.success('تم رفع الملف — يظهر الآن في القائمة ويمكن استعادته');
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(''); }
  };

  const restore = async () => {
    setBusy('restore');
    try {
      await api.restoreBackup(restoreFor.name);
      toast.success('تمت استعادة النسخة. سيُطلب تسجيل الدخول من جديد.');
      setRestoreFor(null);
      // Accounts were restored too: start clean on the restored data.
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(err.message);
      setBusy('');
    }
  };

  const closeReset = () => { setResetStep(0); setResetTyped(''); };
  const freshStart = async () => {
    setBusy('reset');
    try {
      await api.freshStart();
      toast.success('تم حذف كل البيانات. ادخل الآن بحساب «تميز» ورمز الدخول.');
      closeReset();
      // Every account (this one too) is gone: back to the login screen.
      session.clear();
      setTimeout(() => window.location.reload(), 1800);
    } catch (err) {
      toast.error(err.message);
      setBusy('');
    }
  };

  return (
    <PageShell
      title="النسخ الاحتياطي"
      description="نسخ قاعدة البيانات تلقائياً كل يوم، وحفظها على جهازك، واستعادتها"
      actions={(
        <>
          <input ref={fileRef} type="file" accept=".gz,.json,application/gzip,application/json" hidden onChange={upload} />
          <Button variant="secondary" onClick={() => fileRef.current?.click()} loading={busy === 'upload'}>
            <MdUpload size={16} /> رفع نسخة من الجهاز
          </Button>
          <Button onClick={create} loading={busy === 'create'}>
            <MdBackup size={16} /> نسخة احتياطية الآن
          </Button>
        </>
      )}
    >
      <div className="bk-summary">
        <Card>
          <div className="bk-stat">
            <MdSchedule size={22} />
            <div>
              <small>النسخ التلقائي</small>
              <b>كل يوم الساعة ٧:٠٠ صباحاً و ٦:٠٠ مساءً</b>
            </div>
          </div>
        </Card>
        <Card>
          <div className={`bk-stat${data?.last_error ? ' is-bad' : ''}`}>
            <MdBackup size={22} />
            <div>
              <small>آخر نسخة</small>
              <b>{last ? `${fmt(last.created_at)} — ${last.kind_label}` : (loading ? '…' : 'لا توجد نسخة بعد')}</b>
              {data?.last_error && <em>فشلت آخر نسخة تلقائية ({fmt(data.last_error_at)})</em>}
            </div>
          </div>
        </Card>
      </div>

      {error && <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>}

      <Card padded={false}>
        <Table
          keyField="name"
          rows={list}
          loading={loading}
          emptyText="لا توجد نسخ احتياطية بعد"
          searchableKeys={['kind_label', 'by']}
          pageSize={15}
          renderActions={(r) => (
            <>
              <button type="button" className="ui-icon-btn" title="تنزيل إلى الجهاز" disabled={!!busy} onClick={() => download(r)}>
                <MdDownload size={16} />
              </button>
              <button type="button" className="ui-btn-mini ui-btn-mini-warn" disabled={!!busy} onClick={() => setRestoreFor(r)}>
                <MdRestore size={14} style={{ verticalAlign: '-2px' }} /> استعادة
              </button>
            </>
          )}
          columns={[
            { key: 'created_at', label: 'التاريخ', nowrap: true, filterable: false, formatValue: fmt },
            { key: 'kind_label', label: 'النوع', nowrap: true, align: 'center', badge: (v, row) => TONE[row?.kind] || 'neutral' },
            { key: 'by', label: 'بواسطة', nowrap: true, formatValue: (v) => v || '—' },
            { key: 'rows', label: 'السجلات', nowrap: true, align: 'center', filterable: false,
              formatValue: (v) => Number(v || 0).toLocaleString('en-US') },
            { key: 'size', label: 'الحجم', nowrap: true, align: 'center', filterable: false, formatValue: size },
          ]}
        />
      </Card>

      <div className="bk-danger"><Card title="البدء من جديد">
        <div className="bk-danger-row">
          <p>
            حذف كل بيانات المدرسة (الطلاب، العائلات، المعلمين، الشعب، التفقّد، العلامات، الأقساط، الحسابات…)
            والبدء بقاعدة بيانات فارغة، بلا صفوف ولا شعب ولا مواد،
            فيها فقط الفوجان (صباحي، مسائي) وحسابات: صبحي، عمرو، خالد، تميز (مدراء) وموجه.
            تُحفظ نسخة احتياطية كاملة قبل الحذف ويمكن استعادتها من القائمة أعلاه.
          </p>
          <Button variant="danger" onClick={() => setResetStep(1)} disabled={!!busy}>
            <MdDeleteForever size={16} /> البدء من جديد
          </Button>
        </div>
      </Card></div>

      <ConfirmModal
        open={resetStep === 1}
        title="البدء من جديد؟"
        tone="danger"
        message="سيتم حذف كل بيانات المدرسة وكل الحسابات، وستخرج من النظام. هل أنت متأكد؟"
        confirmText="نعم، متابعة"
        onCancel={closeReset}
        onConfirm={() => setResetStep(2)}
      />

      <Modal
        open={resetStep === 2}
        title="تأكيد أخير"
        size="sm"
        onClose={busy === 'reset' ? undefined : closeReset}
        footer={(
          <>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={closeReset} disabled={busy === 'reset'}>إلغاء</button>
            <button
              type="button"
              className="ui-btn ui-btn-danger"
              onClick={freshStart}
              disabled={resetTyped.trim() !== RESET_PHRASE || busy === 'reset'}
            >
              {busy === 'reset' ? '...' : 'حذف كل البيانات الآن'}
            </button>
          </>
        )}
      >
        <p className="ui-modal-message">
          للتأكيد اكتب: <b>{RESET_PHRASE}</b>
        </p>
        <Input value={resetTyped} onChange={(e) => setResetTyped(e.target.value)} placeholder={RESET_PHRASE} autoFocus />
      </Modal>

      <ConfirmModal
        open={!!restoreFor}
        title="استعادة نسخة احتياطية"
        tone="danger"
        message={restoreFor
          ? `سيتم استبدال جميع بيانات المدرسة ببيانات نسخة ${fmt(restoreFor.created_at)}. تُحفظ نسخة من الوضع الحالي تلقائياً قبل الاستعادة، ويمكن الرجوع إليها من هذه الصفحة.`
          : ''}
        confirmText="استعادة"
        busy={busy === 'restore'}
        onCancel={() => setRestoreFor(null)}
        onConfirm={restore}
      />
    </PageShell>
  );
}
